import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { GrupoDePrivilegioService } from '../../../services/usuario/grupo-de-privilegio.service';
import { PrivilegioService } from '../../../services/usuario/privilegio.service';
import { MessageService } from 'primeng/api';
import { GrupoDePrivilegioDTO } from '../../../models/grupo-de-privilegio.model';
import { PrivilegioDTO } from '../../../models/privilegio.model';
import { agruparPrivilegiosPorSecao, SecaoDePrivilegio } from '../../../enums/SecaoPrivilegio.enum';
import { Subscription, forkJoin } from 'rxjs';

@Component({
  selector: 'app-grupo-de-privilegio-edit',
  templateUrl: './grupo-de-privilegio-edit.component.html',
  styleUrl: './grupo-de-privilegio-edit.component.scss',
})
export class GrupoDePrivilegioEditComponent implements OnInit, OnDestroy {
  form: Partial<GrupoDePrivilegioDTO> = {
    nome: '',
    status: true,
  };

  selectedPrivilegios: string[] = [];
  privilegiosList: PrivilegioDTO[] = [];
  /** privilegiosList agrupada por seção, na ordem em que as telas aparecem no menu. */
  secoesPrivilegio: SecaoDePrivilegio[] = [];
  allSelected = false;

  // Objetos de traducao
  texts = {
    titulo: '',
    subtitulo: '',
    dados_do_grupo: ''
  };

  labels = {
    nome_do_grupo: '',
    privilegios: '',
    marcar_todas: ''
  };

  placeholders = {
    adicione_um_nome: ''
  };

  botoes = {
    voltar: '',
    salvar: ''
  };

  private langChangeSub!: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private grupoDePrivilegioService: GrupoDePrivilegioService,
    private privilegioService: PrivilegioService,
    private messageService: MessageService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.loadTranslations();

    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      this.loadPrivilegiosEGrupo(Number(id));
    } else {
      this.loadPrivilegios();
    }
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('grupo-de-privilegio-edit.texts.titulo'),
      subtitulo: this.translate.instant('grupo-de-privilegio-edit.texts.subtitulo'),
      dados_do_grupo: this.translate.instant('grupo-de-privilegio-edit.texts.dados_do_grupo')
    };

    this.labels = {
      nome_do_grupo: this.translate.instant('grupo-de-privilegio-edit.labels.nome_do_grupo'),
      privilegios: this.translate.instant('grupo-de-privilegio-edit.labels.privilegios'),
      marcar_todas: this.translate.instant('grupo-de-privilegio-edit.labels.marcar_todas')
    };

    this.placeholders = {
      adicione_um_nome: this.translate.instant('grupo-de-privilegio-edit.placeholders.adicione_um_nome')
    };

    this.botoes = {
      voltar: this.translate.instant('grupo-de-privilegio-edit.botoes.voltar'),
      salvar: this.translate.instant('grupo-de-privilegio-edit.botoes.salvar')
    };

    // Os títulos das seções também são traduzidos: remonta ao trocar de idioma.
    this.montarSecoes();
  }

  /** Reagrupa os privilégios por seção. Chamar sempre que a lista ou o idioma mudar. */
  private montarSecoes(): void {
    this.secoesPrivilegio = agruparPrivilegiosPorSecao(
      this.privilegiosList,
      (chave) => this.translate.instant(`grupo-de-privilegio-edit.secoes.${chave}`)
    );
  }

  /** Carrega lista de janelas e dados do grupo em paralelo, garantindo que
   *  privilegiosList esteja pronta antes de processar selectedPrivilegios. */
  private loadPrivilegiosEGrupo(id: number): void {
    forkJoin({
      janelas: this.privilegioService.getJanelasDeMenu(),
      grupo: this.grupoDePrivilegioService.getById(`/grupoDePrivilegio/${id}`)
    }).subscribe({
      next: ({ janelas, grupo }) => {
        this.privilegiosList = (janelas ?? []).map(j => ({
          id: j.id,
          codigoPrivilegio: 0,
          janelaDeMenu: j,
          consulta: true
        } as PrivilegioDTO));

        this.form = {
          id: grupo.id,
          nome: grupo.nome,
          status: grupo.status,
        };

        // Extrair o enum da janelaDeMenu (mesmo padrão do componente inline)
        this.selectedPrivilegios = (grupo.privilegios ?? [])
          .map(p => {
            const jm = p.janelaDeMenu as any;
            return jm?.enum ?? jm;
          })
          .filter(Boolean) as string[];

        this.montarSecoes();
        this.allSelected = this.selectedPrivilegios.length === this.privilegiosList.length;
      },
      error: (err) => {
        console.error('Erro ao carregar o Grupo de Privilégio:', err);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.translate.instant('grupo-de-privilegio.mensagens.erro_carregar'),
        });
        this.router.navigate(['/grupo-de-privilegio']);
      },
    });
  }

  loadPrivilegios(): void {
    // Busca todos os 20 valores do enum JanelaDeMenu diretamente,
    // sem depender dos registros que existem no banco de dados
    this.privilegioService.getJanelasDeMenu().subscribe({
      next: (janelas) => {
        this.privilegiosList = (janelas ?? []).map(j => ({
          id: j.id,
          codigoPrivilegio: 0,
          janelaDeMenu: j,
          consulta: true
        } as PrivilegioDTO));
        this.montarSecoes();
        this.allSelected = this.selectedPrivilegios.length === this.privilegiosList.length;
      },
      error: (err) => {
        console.error('Erro ao carregar privilégios:', err);
      },
    });
  }

  toggleAllPrivileges(selected: boolean): void {
    this.allSelected = selected;
    this.selectedPrivilegios = selected
      ? (this.privilegiosList || []).map(p => p.janelaDeMenu?.enum).filter(Boolean) as string[]
      : [];
  }

  submit(): void {
    if (!this.form.nome) {
      this.messageService.add({
        severity: 'error',
        summary: this.translate.instant('common.erro'),
        detail: this.translate.instant('grupo-de-privilegio.mensagens.campo_nome_obrigatorio'),
      });
      return;
    }

    const body: GrupoDePrivilegioDTO = {
      id: this.form.id,
      nome: this.form.nome,
      status: this.form.status ?? true,
      // Mesmo formato do componente inline que funciona
      privilegios: this.selectedPrivilegios.map(enumValue => ({
        janelaDeMenu: enumValue,
        consulta: true
      })) as any,
    };

    this.grupoDePrivilegioService.put(`/grupoDePrivilegio/${this.form.id}`, body).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.translate.instant('common.sucesso'),
          detail: this.translate.instant('grupo-de-privilegio.mensagens.grupo_editado'),
        });
        this.router.navigate(['/grupo-de-privilegio']);
      },
      error: (err) => {
        console.error('Erro ao editar o Grupo de Privilégio:', err);
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.translate.instant('grupo-de-privilegio.mensagens.erro_editar'),
        });
      },
    });
  }

}
